import React, { useCallback, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { connect } from 'react-redux';
import {
  Button,
  Input,
} from 'reactstrap';
import { useIntl } from 'react-intl';

import { clearDroppedFiles } from '../../actions/App';

import {
  accountUsageLock,
  subscriptionExpired,
} from '../../helpers/App';
import { getSetting } from '../../helpers/Settings';

const UploadPageUploadButton = ({
  profile,
  droppedFiles,
  uploadPageDragDropEnabled,
  resetDroppedFiles,
  submittedFilesCount,
  submitFiles,
  disabled,
  finished,
}) => {
  const intl = useIntl();
  const fileInput = useRef();

  useEffect(() => {
    fileInput.current.value = null;
  }, [submittedFilesCount]);

  const submitFilesFromInput = (e) => {
    const { files } = e.target;

    if (accountUsageLock(profile.client) || subscriptionExpired(profile.client)) {
      return;
    }

    if (files.length > 0) {
      submitFiles(files);
    }
  };

  const submitFilesFromDrop = useCallback(() => {
    if (droppedFiles.length > 0) {
      if (accountUsageLock(profile.client) || subscriptionExpired(profile.client)) {
        return;
      }

      if (uploadPageDragDropEnabled) {
        submitFiles(droppedFiles);
      }
      resetDroppedFiles();
    }
  }, [profile, droppedFiles, uploadPageDragDropEnabled, resetDroppedFiles, submitFiles]);

  useEffect(() => {
    submitFilesFromDrop();
  }, [droppedFiles, submitFilesFromDrop]);

  const triggerInputFile = () => {
    if (!accountUsageLock(profile.client) && !subscriptionExpired(profile.client)) {
      fileInput.current.click();
    }
  };

  return (
    <div className="upload-button-wrapper bordered">
      {(submittedFilesCount === 0 || finished) && (
        <i className="far fa-upload fa-4x" />
      )}
      <Input
        type="file"
        multiple={true}
        name="file"
        className="upload-file"
        onChange={submitFilesFromInput}
        innerRef={fileInput}
        accept={getSetting('allowed_extensions').map(e => (`.${e}`)).join(',')}
        disabled={disabled}
      />
      <Button color="primary" onClick={triggerInputFile} size="lg" className={classnames({ disabled })}>
        {submittedFilesCount === 0 ? (
          <span>{intl.formatMessage({ defaultMessage: 'Upload files' })}</span>
        ) : (
          <span>{intl.formatMessage({ defaultMessage: 'Add more files' })}</span>
        )}
      </Button>
      <p>{intl.formatMessage({ defaultMessage: 'or drag and drop files to upload' })}</p>
    </div>
  );
};

const mapStateToProps = state => ({
  profile: state.Auth.profile,
  droppedFiles: state.App.droppedFiles,
  uploadPageDragDropEnabled: state.UploadPages.dragDropEnabled,
});

const mapDispatchToProps = dispatch => ({
  resetDroppedFiles: () => {
    dispatch(clearDroppedFiles());
  },
});

UploadPageUploadButton.propTypes = {
  profile: PropTypes.object,
  droppedFiles: PropTypes.array.isRequired,
  uploadPageDragDropEnabled: PropTypes.bool.isRequired,
  resetDroppedFiles: PropTypes.func.isRequired,
  submittedFilesCount: PropTypes.number.isRequired,
  submitFiles: PropTypes.func.isRequired,
  disabled: PropTypes.bool.isRequired,
  finished: PropTypes.bool.isRequired,
};

UploadPageUploadButton.defaultProps = {
  profile: null,
};

export default connect(mapStateToProps, mapDispatchToProps)(UploadPageUploadButton);
